import {
  useLocalStorageState,
  useLocalStorageReference,
} from "@outschool/local-storage";
import { useCallback, useMemo } from "react";
import _ from "lodash";
import { userUsesSelfElectedPublicFundingOptions } from "@outschool/funding-programs-shared";

export type SelfElectedStateFundingProgram = {
  stateAbbreviation: string;
  stateName: string;
  fundingOptions: {
    statePortal: boolean;
    stateDebitCard: boolean;
    stateProgramName: string;
    otherProgramName: string;
    reimbursement: boolean;
    charter: boolean;
    selfPay: boolean;
  };
  source: string;
};

type SelfElectedStateFundingProgramBanner = SelfElectedStateFundingProgram & {
  searchFiltersActive: boolean;
  hideBanner: boolean;
  // if true, we don't want to save the default data
  hasRejectedStateFundingQuestions: boolean;
};

const defaultSelfElectedStateFundingProgramBanner: SelfElectedStateFundingProgramBanner =
  {
    stateAbbreviation: "",
    stateName: "",
    fundingOptions: {
      statePortal: false,
      stateDebitCard: false,
      stateProgramName: "",
      otherProgramName: "",
      reimbursement: false,
      charter: false,
      selfPay: false,
    },
    source: "",
    searchFiltersActive: false,
    hideBanner: false,
    hasRejectedStateFundingQuestions: false,
  };

// example self elected state funding program
// {
//     searchFiltersActive: true,
//     stateAbbreviation: "AR",
//     stateName: "Arkansas",
//     fundingOptions: {
//         statePortal: true,
//         reimbursement: false,
//         charter: false,
//         selfPay: true
//     }
// }
// stringified:
// {"searchFiltersActive":true,"stateAbbreviation":"AR","stateName": "Arkansas","fundingOptions":{"statePortal":true,"reimbursement":false,"charter":false,"selfPay":true}}
// key: self-elected-state-funding-program

/**
 * This hook is used to get and set the self-elected state funding program info for a logged out user
 * The information is stored in local storage and the info once set can (if the funding is state not charter) affect the search filters applied
 * @returns
 */
export function useSelfElectedStateFundingProgram() {
  const { localStorage } = useLocalStorageReference();
  const [selfElectedStateFundingProgram, setSelfElectedStateFundingProgram] =
    useLocalStorageState<SelfElectedStateFundingProgramBanner>(
      "self-elected-state-funding-program",
      defaultSelfElectedStateFundingProgramBanner,
    );

  const defaultState = useMemo(() => {
    return {
      searchFiltersActive: false,
      stateAbbreviation: "",
      stateName: "",
      fundingOptions: {
        statePortal: false,
        stateDebitCard: false,
        stateProgramName: "",
        otherProgramName: "",
        reimbursement: false,
        charter: false,
        selfPay: false,
      },
      source: "",
      hideBanner: false,
      hasRejectedStateFundingQuestions: false,
    };
  }, []);

  // Read directly from localStorage to avoid React state synchronization issues
  const getSelfElectedStateFundingProgramBannerOrDefault =
    useCallback((): SelfElectedStateFundingProgramBanner => {
      const storedValue = localStorage?.getItem(
        "self-elected-state-funding-program",
      );
      let selfElectedStateFundingProgramBanner =
        defaultSelfElectedStateFundingProgramBanner;
      if (storedValue) {
        const parsedValue = JSON.parse(storedValue);
        selfElectedStateFundingProgramBanner = {
          ...selfElectedStateFundingProgramBanner,
          ...parsedValue,
        };
      }
      return selfElectedStateFundingProgramBanner;
    }, [localStorage]);

  const activateSearchFilters = useCallback(() => {
    setSelfElectedStateFundingProgram({
      ...getSelfElectedStateFundingProgramBannerOrDefault(),
      searchFiltersActive: true,
    });
  }, [
    getSelfElectedStateFundingProgramBannerOrDefault,
    setSelfElectedStateFundingProgram,
  ]);

  const deactivateSearchFilters = useCallback(() => {
    setSelfElectedStateFundingProgram({
      ...getSelfElectedStateFundingProgramBannerOrDefault(),
      searchFiltersActive: false,
    });
  }, [
    getSelfElectedStateFundingProgramBannerOrDefault,
    setSelfElectedStateFundingProgram,
  ]);

  const hideBanner = useCallback(
    (
      {
        withRejection,
        resetState,
      }: {
        withRejection: boolean;
        resetState: boolean;
      } = { withRejection: false, resetState: false },
    ) => {
      const newState = resetState
        ? defaultState
        : getSelfElectedStateFundingProgramBannerOrDefault();

      setSelfElectedStateFundingProgram({
        ...newState,
        hideBanner: true,
        hasRejectedStateFundingQuestions: withRejection,
      });
    },
    [
      getSelfElectedStateFundingProgramBannerOrDefault,
      setSelfElectedStateFundingProgram,
      defaultState,
    ],
  );

  const setState = useCallback(
    (
      stateAbbreviation: string,
      stateName: string,
      activateSearchFilters: boolean = true,
    ) => {
      setSelfElectedStateFundingProgram({
        ...getSelfElectedStateFundingProgramBannerOrDefault(),
        stateAbbreviation,
        stateName,
        searchFiltersActive: activateSearchFilters,
      });
    },
    [
      getSelfElectedStateFundingProgramBannerOrDefault,
      setSelfElectedStateFundingProgram,
    ],
  );

  const resetState = useCallback(() => {
    setSelfElectedStateFundingProgram(defaultState);
  }, [setSelfElectedStateFundingProgram, defaultState]);

  const setFundingOptions = useCallback(
    (
      key: keyof SelfElectedStateFundingProgramBanner["fundingOptions"],
      value: boolean | string | undefined,
    ) => {
      const selfElectedStateFundingProgram =
        getSelfElectedStateFundingProgramBannerOrDefault();
      const updatedSelfElectedStateFundingProgram = {
        ...selfElectedStateFundingProgram,
        fundingOptions: {
          ...selfElectedStateFundingProgram.fundingOptions,
          [key]: value,
        },
      };

      if (updatedSelfElectedStateFundingProgram.fundingOptions.statePortal) {
        updatedSelfElectedStateFundingProgram.searchFiltersActive = true;
      }

      setSelfElectedStateFundingProgram(updatedSelfElectedStateFundingProgram);
    },
    [
      getSelfElectedStateFundingProgramBannerOrDefault,
      setSelfElectedStateFundingProgram,
    ],
  );

  const setSource = useCallback(
    (source: string) => {
      const selfElectedStateFundingProgram =
        getSelfElectedStateFundingProgramBannerOrDefault();
      setSelfElectedStateFundingProgram({
        ...selfElectedStateFundingProgram,
        source,
      });
    },
    [
      getSelfElectedStateFundingProgramBannerOrDefault,
      setSelfElectedStateFundingProgram,
    ],
  );

  const usesPublicFundingOrCharter = useCallback(() => {
    const selfElectedStateFundingProgram =
      getSelfElectedStateFundingProgramBannerOrDefault();
    return userUsesSelfElectedPublicFundingOptions(
      selfElectedStateFundingProgram,
    );
  }, [getSelfElectedStateFundingProgramBannerOrDefault]);

  const usesCharter = useCallback(() => {
    const selfElectedStateFundingProgram =
      getSelfElectedStateFundingProgramBannerOrDefault();
    return Boolean(selfElectedStateFundingProgram.fundingOptions.charter);
  }, [getSelfElectedStateFundingProgramBannerOrDefault]);

  const createAccountVariables = useCallback(() => {
    const currentState = getSelfElectedStateFundingProgramBannerOrDefault();

    // if they have rejected the banner or not set anything we don't want to save the default data
    if (
      currentState.hasRejectedStateFundingQuestions ||
      currentState.stateAbbreviation === ""
    ) {
      return undefined;
    }

    return _.omit(
      currentState,
      "searchFiltersActive",
      "hideBanner",
      "hasRejectedStateFundingQuestions",
    );
  }, [getSelfElectedStateFundingProgramBannerOrDefault]);

  return {
    selfElectedStateFundingProgram,
    setSelfElectedStateFundingProgram,
    activateSearchFilters,
    deactivateSearchFilters,
    setFundingOptions,
    setSource,
    hideBanner,
    setState,
    usesCharter,
    usesPublicFundingOrCharter,
    createAccountVariables,
    resetState,
  };
}
